import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  BankDetails,
  BankDetailsFilter,
  BankDetailsPayload,
  BankDetailsResponse,
} from "../types/banks.types";

type BankDetailsDetailResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: BankDetails;
};

type BankDetailsMutationResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: unknown;
};

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.bankDetails;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const banksService = {
  async list(filter: BankDetailsFilter): Promise<BankDetailsResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<BankDetailsResponse>(
        endpoints.list(params.toString()),
        { method: "GET", isAuthenticated: true, cache: "no-store" },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to load bank details due to an unexpected error.",
      ) as unknown as BankDetailsResponse;
    }
  },

  async details(id: number | string): Promise<BankDetailsDetailResponse> {
    try {
      return await apiClient<BankDetailsDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load bank details due to an unexpected error.",
      ) as unknown as BankDetailsDetailResponse;
    }
  },

  async create(
    payload: Partial<BankDetailsPayload>,
  ): Promise<BankDetailsMutationResponse> {
    try {
      return await apiClient<BankDetailsMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to add bank details due to an unexpected error.",
      ) as BankDetailsMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: Partial<BankDetailsPayload>,
  ): Promise<BankDetailsMutationResponse> {
    try {
      return await apiClient<BankDetailsMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update bank details due to an unexpected error.",
      ) as BankDetailsMutationResponse;
    }
  },

  async delete(id: number | string): Promise<BankDetailsMutationResponse> {
    try {
      return await apiClient<BankDetailsMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete bank details due to an unexpected error.",
      ) as BankDetailsMutationResponse;
    }
  },

  async setPrimary(id: number): Promise<BankDetailsMutationResponse> {
    try {
      return await apiClient<BankDetailsMutationResponse>(endpoints.setPrimary(id), {
        method: "PATCH",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to set primary bank due to an unexpected error.",
      ) as BankDetailsMutationResponse;
    }
  },

  async verify(
    id: number,
    payload: { status: "verified" | "rejected" },
  ): Promise<BankDetailsMutationResponse> {
    try {
      return await apiClient<BankDetailsMutationResponse>(endpoints.verify(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to verify bank details due to an unexpected error.",
      ) as BankDetailsMutationResponse;
    }
  },
};
